import { useNavigator } from '@repo/toolkit/use-navigator'
import { FormIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getEnrollment } from '../../../api/enrollments/get-enrollment'
import { updateEnrollment } from '../../../api/enrollments/update-enrollment'
import { ItemNotFound } from '../../../components/elements/item-not-found'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import EnrollmentOverpaymentRefundModal from '../../../components/pages/enrollment-profile/shared/enrollment-overpayment-refund-modal'
import UpdateEnrollmentForm from '../../../components/pages/enrollments/enrollment-form/update-enrollment-form'
import { Paths } from '../../../lib/constants/path'
import useAuth from '../../../lib/hooks/use-auth'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { enrollmentChildNavbarFields } from '../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentInput } from '../../../lib/types/models/enrollment/enrollment-input'
import { UpdateInput } from '../../../lib/types/utils'
import { updateButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import NotFoundPage from '../../common/not-found'

const EnrollmentUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { navigate, back } = useNavigator()
  const { userRole } = useAuth()

  const { mutateAsync } = useMutation({
    mutationFn: (input: UpdateInput<EnrollmentInput>) => updateEnrollment(input, ['id']),
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollment'] })
      toast.success('Dossier modifié avec succès')
    }
  })

  const { data: enrollment, isLoading } = useQuery({
    queryKey: ['enrollment', id, enrollmentChildNavbarFields],
    queryFn: () => getEnrollment(Number(id), enrollmentChildNavbarFields)
  })

  if (!id || isNaN(+id)) return <ItemNotFound title="Identifiant de dossier invalide" />
  if (isLoading) return <LoadingScreen />
  if (!enrollment) return <NotFoundPage />
  return (
    <div className={'flex flex-col gap-4'}>
      <UserNavBar
        icon={FormIcon}
        title={`${EnrollmentProperties.getEnrollmentTitleWithoutAA(enrollment)}${
          enrollment.isAA ? ' - ' : ''
        }`}
        badgeTitle={EnrollmentProperties.getEnrollmentStatusBadge(enrollment).label}
        badgeVariant={EnrollmentProperties.getEnrollmentStatusBadge(enrollment).variant}
        phrase={enrollment.isAA ? 'AA' : undefined}
        phraseVariant={'blue'}
      />
      <UpdateEnrollmentForm
        enrollmentId={+id}
        buttonsConfig={updateButtonsConfig({
          onCancel: () => back(Paths.ENROLLMENT_LIST_PAGE),
          onUpdate: (values) => mutateAsync({ id: +id, ...values }),
          onUpdateThenCreate: async (values) => {
            await mutateAsync({ id: +id, ...values })
            navigate(Paths.ENROLLMENT_CREATE_PAGE)
          },
          onUpdateThenList: async (values) => {
            await mutateAsync({ id: +id, ...values })
            back(Paths.ENROLLMENT_LIST_PAGE)
          }
        })}
      />
      {/* refunds are an admin action */}
      {userRole === RoleEnum.ADMIN && <EnrollmentOverpaymentRefundModal enrollmentId={+id} />}
    </div>
  )
}

export default EnrollmentUpdatePage
